import { TinypassSDK } from "@product/rosetta-sdk";
import first from "lodash/first";
import { fetchQuery, graphql, readInlineData } from "react-relay";
import type RelayModernEnvironment from "relay-runtime/lib/store/RelayModernEnvironment";

import type { helpersRosettaArticleContextArticle$key } from "scmp-app/queries/__generated__/helpersRosettaArticleContextArticle.graphql";
import type {
  helpersRosettaPaywallArticleSearchQuery,
  helpersRosettaPaywallArticleSearchQuery$variables,
} from "scmp-app/queries/__generated__/helpersRosettaPaywallArticleSearchQuery.graphql";

export type ArticleContext = {
  sections?: string[];
  topics?: string[];
};

export const setPianoCustomVariable = (key: string, value: unknown) => {
  TinypassSDK.instance?.push(["setCustomVariable", key, value]);
};

export const buildArticleContext = (
  reference: helpersRosettaArticleContextArticle$key | null | undefined,
): ArticleContext | null => {
  const article = readInlineData(
    graphql`
      fragment helpersRosettaArticleContextArticle on Content @inline {
        topics {
          name
        }
        sectionsV2 {
          value {
            ... on Section {
              name
            }
            ... on VideoSection {
              name
            }
          }
        }
      }
    `,
    reference,
  );
  if (!article) return null;
  return {
    sections:
      article.sectionsV2
        ?.map(section =>
          section?.value
            ?.map(segment => segment?.name)
            .filter((name): name is string => !!name)
            .join("/"),
        )
        .filter((section): section is string => !!section) ?? [],
    topics:
      article.topics?.map(topic => topic?.name).filter((name): name is string => !!name) ?? [],
  };
};

export const searchArticles = async ({
  environment,
  filter,
}: {
  environment: RelayModernEnvironment;
  filter: {
    sectionIds?: string[];
    size?: number;
  };
}) => {
  const variables: helpersRosettaPaywallArticleSearchQuery$variables = {
    first: filter?.size ?? 10,
    sectionIds: filter?.sectionIds ?? [],
  };

  const sectionArticlesData = await fetchQuery<helpersRosettaPaywallArticleSearchQuery>(
    environment,
    relatedSectionQuery,
    variables,
  ).toPromise();

  const articles =
    sectionArticlesData?.articles?.edges?.map(edge => ({
      entityId: edge.node.entityId,
      headline: edge.node.headline,
      imageUrl: first(edge?.node?.images)?.url,
    })) ?? [];

  return {
    articles,
  };
};

const relatedSectionQuery = graphql`
  query helpersRosettaPaywallArticleSearchQuery($first: Int!, $sectionIds: [String]) {
    articles(
      first: $first
      filter: { sectionIds: $sectionIds }
      orderBy: { field: PUBLISHED_DATE, direction: DESC }
    ) {
      edges {
        node {
          entityId
          headline
          images {
            url
          }
        }
      }
    }
  }
`;
